/**
 * How the muurikka reaches the player from wherever they happen to be
 * standing.
 *
 * The fourth of the placement sums and the shortest-ranged. It borrows one
 * half from each of the two before it, which is why it is neither of them:
 *
 *   * The *level* is the karaoke's pair of laws - an inverse-distance near
 *     field under a ramp to silence, whichever is quieter. A pan is a point in
 *     the open with a place you stand at, so it needs both: the ramp on its own
 *     is far too flat inside the last few metres, and the inverse law on its
 *     own never actually reaches zero.
 *   * The *wall* is the tent's `indoors()`, not the karaoke's `insideVenue()`.
 *     This thing is outdoors, so every roof in the level is between it and
 *     somebody rather than only the navetta's.
 *   * The *cutoff* is the tent's geometric interpolation, driven off the ramp
 *     rather than off the level. See `MUURIKKA_SIHINA.nearCutoff` for why those
 *     two questions come apart here and nowhere else.
 *
 * Pure, for the same reason `karaoke.ts`, `teltta.ts`, `yard.ts` and `pace.ts`
 * are: the interesting part is the arithmetic, and geometry is worth testing
 * without a browser.
 *
 * Presentation only. Nothing here is reachable from `src/sim`.
 */

import { MUURIKKA_SIHINA } from '../config/audio';
import { clamp } from '../utils/math';
import type { ListenerMix } from './karaoke';
import { indoors } from './yard';

/** Distance from the plate, in metres, ignoring height. */
export function muurikkaDistance(x: number, z: number): number {
  return Math.hypot(x - MUURIKKA_SIHINA.source.x, z - MUURIKKA_SIHINA.source.z);
}

/**
 * How much of the way in you are, 0 at `silent` and 1 from `full` on. Both
 * numbers are read off this one, which is what keeps them from disagreeing
 * about where the pan is.
 */
function reach(distance: number): number {
  const { full, silent } = MUURIKKA_SIHINA;
  return clamp((silent - distance) / (silent - full), 0, 1);
}

export function muurikkaMix(x: number, z: number): ListenerMix {
  const { full, nearCutoff, farCutoff } = MUURIKKA_SIHINA;
  const distance = muurikkaDistance(x, z);
  const ramp = reach(distance);

  // The karaoke's own argument, over a fifth of its range: squared because
  // sound does not thin out evenly with distance, and floored by the inverse
  // law because across the last few metres of an approach the ramp is far too
  // flat - it would leave the pan at four fifths of itself six metres out,
  // which is a grill you walk up to without ever hearing yourself arrive. The
  // two meet at `full` and the quieter wins, so the seam is continuous.
  const carry = Math.min(ramp * ramp, full / Math.max(full, distance));

  // Geometric, and off the ramp rather than off `carry`. A pan keeps its top
  // for as long as you can hear it at all and only smears at the edge of its
  // range; tying this to the level would have it dull at nine metres. See the
  // config.
  const open = farCutoff * Math.pow(nearCutoff / farCutoff, ramp);

  // The wall scales the level and *caps* the top, the way the tent's does: a
  // wall cannot make the far end of the yard brighter than the distance has
  // already left it.
  const wall = indoors(x, z) ? MUURIKKA_SIHINA.inside : MUURIKKA_SIHINA.outside;
  return { gain: carry * wall.gain, cutoff: Math.min(open, wall.cutoff) };
}

/**
 * What the mixer plays before the game has said where anybody is standing.
 *
 * Silent, like the tent's and the karaoke's: the run starts indoors and 138 m
 * off, which is seven times this sound's whole range, and a default that is too
 * loud is a working grill in a bedroom for one frame.
 */
export const MUURIKKA_SILENT_MIX: ListenerMix = { gain: 0, cutoff: MUURIKKA_SIHINA.farCutoff };
